import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AddressBook from '../components/AddressBook';
import LazyImage from '../components/LazyImage';
import { PickupLocationSelector } from '../components/checkout/PickupLocationSelector';
import CouponSuccessPopper from '../components/CouponSuccessPopper';
import type { Address } from '../contexts/AddressContext';
import { useAddress } from '../contexts/AddressContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../contexts/ToastContext';
import {
  sanitizeInput
} from '../utils/validation';
import { couponService, orderService, companyService } from '../services';
import { gtagService } from '../services/gtagService';
import type { PaymentMethod } from '../services/orderService';
import type { CompanyInfo } from '../services/companyService';

interface CheckoutFormData {
  name: string;
  email: string;
  couponCode: string;
}

interface CouponData {
  code: string;
  discountValue: number;
  discountType: 'percentage' | 'fixed';
  minimumOrderAmount?: number;
  maximumDiscount?: number;
  isValid: boolean;
  message?: string;
}

const Checkout: React.FC = () => {
  const { items, total, clearCart } = useCart();
  const { getSelectedAddress, addresses } = useAddress();
  const { user, isLoading: authLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [showAddressBook, setShowAddressBook] = useState(false);
  const [formData, setFormData] = useState<CheckoutFormData>({
    name: user?.name || 'Guest User',
    email: user?.email || '',
    couponCode: '',
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [appliedCoupon, setAppliedCoupon] = useState<CouponData | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [selectedPickupLocation, setSelectedPickupLocation] = useState<string | null>(null);
  const [pickupInstructions, setPickupInstructions] = useState<string>('');
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [selectedTransportProvider, setSelectedTransportProvider] = useState<string>('');
  const [showCouponPopper, setShowCouponPopper] = useState(false);
  const [popperData, setPopperData] = useState<{ amount: number; code: string }>({ amount: 0, code: '' });
  const [customerNotes, setCustomerNotes] = useState<string>('');

  // Auto-select default address on component mount
  useEffect(() => {
    // Don't try to select address while auth is loading
    if (authLoading) return;
    
    const defaultAddress = getSelectedAddress();
    if (defaultAddress) {
      setSelectedAddress(defaultAddress);
    } else if (addresses.length > 0) {
      setSelectedAddress(addresses[0]);
    } else {
      setShowAddressBook(true);
    }
  }, [addresses.length, authLoading]); // Remove getSelectedAddress from dependencies

  // Check for stored coupon on mount and fetch company info
  useEffect(() => {
    // Track begin checkout event
    if (items.length > 0) {
      gtagService.trackBeginCheckout(items, total);
    }
    
    const storedCoupon = couponService.getStoredCoupon();
    if (storedCoupon && !appliedCoupon) {
      setFormData(prev => ({ ...prev, couponCode: storedCoupon }));
      // Auto-apply the stored coupon
      handleApplyCoupon();
    }
    
    // Fetch company info for packaging charges
    const fetchCompanyInfo = async () => {
      const response = await companyService.getCompanyInfo();
      if (response.success) {
        setCompanyInfo(response.data);
      }
    };
    fetchCompanyInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    let sanitizedValue = sanitizeInput(value);
    
    // Additional sanitization for specific fields
    if (name === 'email') {
      sanitizedValue = sanitizedValue.slice(0, 254);
    }
    
    setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };


  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate address selection
    if (!selectedAddress) {
      showToast('Please select a delivery address', 'warning');
      return false;
    }

    // Validate transport provider selection (MANDATORY)
    if (!selectedTransportProvider) {
      showToast('Please select a transport provider', 'error');
      return false;
    }

    // Pickup location is now optional - pickup instructions can handle cases where no location is selected

    // No need to validate name or email anymore since we get them from user registration

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddressSelect = (address: Address) => {
    setSelectedAddress(address);
    setShowAddressBook(false);
    // Clear pickup location selection when manually changing address
    setSelectedPickupLocation(null);
    setPickupInstructions('');
  };

  const validateCoupon = async (code: string): Promise<CouponData | null> => {
    try {
      const response = await couponService.validateCoupon({
        code: code.toUpperCase(),
        orderAmount: total,
        customerId: user?.id || 'guest',
        productIds: items.map(item => item.id),
        productQuantities: items.reduce((acc, item) => ({
          ...acc,
          [item.id]: item.quantity
        }), {})
      });

      if (!response.success || !response.data) {
        return null;
      }

      const validationData = response.data;
      
      if (!validationData.isValid) {
        return {
          code: code,
          discountValue: validationData.discountValue || 0,
          discountType: validationData.discountType || 'fixed',
          minimumOrderAmount: validationData.minimumOrderAmount,
          maximumDiscount: validationData.maximumDiscount,
          isValid: false,
          message: validationData.message
        };
      }

      return {
        code: validationData.code || code,
        discountValue: validationData.discountValue || 0,
        discountType: validationData.discountType || 'fixed',
        minimumOrderAmount: validationData.minimumOrderAmount,
        maximumDiscount: validationData.maximumDiscount,
        isValid: true,
        message: validationData.message
      };
    } catch (error) {
      console.error('Error validating coupon:', error);
      return null;
    }
  };

  const handleApplyCoupon = async () => {
    if (!formData.couponCode.trim()) {
      setErrors(prev => ({ ...prev, coupon: 'Please enter a coupon code' }));
      return;
    }

    setCouponLoading(true);
    setErrors(prev => ({ ...prev, coupon: '' }));

    try {
      const validatedCoupon = await validateCoupon(formData.couponCode);
      
      if (!validatedCoupon) {
        setErrors(prev => ({ ...prev, coupon: 'Invalid coupon code' }));
        setAppliedCoupon(null);
      } else if (!validatedCoupon.isValid) {
        const errorMessage = validatedCoupon.message || 
          (validatedCoupon.minimumOrderAmount ? `Minimum order amount: ₹${validatedCoupon.minimumOrderAmount}` : 'Coupon is not valid');
        setErrors(prev => ({ ...prev, coupon: errorMessage }));
        setAppliedCoupon(null);
      } else {
        setAppliedCoupon(validatedCoupon);
        setErrors(prev => ({ ...prev, coupon: '' }));
        
        // Calculate discount amount
        const discountAmount = couponService.calculateDiscount(
          total,
          validatedCoupon.discountType,
          validatedCoupon.discountValue,
          validatedCoupon.minimumOrderAmount,
          validatedCoupon.maximumDiscount
        ).discountAmount;
        
        // Show popper animation
        setPopperData({ amount: discountAmount, code: validatedCoupon.code });
        setShowCouponPopper(true);
        
        // Also show toast for accessibility
        showToast('Coupon applied successfully!', 'success');
        // Store the coupon in session
        couponService.storeAppliedCoupon(validatedCoupon.code);
      }
    } catch {
      setErrors(prev => ({ ...prev, coupon: 'Error validating coupon' }));
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setFormData(prev => ({ ...prev, couponCode: '' }));
    setErrors(prev => ({ ...prev, coupon: '' }));
    couponService.clearStoredCoupon();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Calculate final total
    const subtotal = total;
    let discountAmount = 0;
    if (appliedCoupon && appliedCoupon.isValid) {
      const calculatedDiscount = couponService.calculateDiscount(
        subtotal,
        appliedCoupon.discountType,
        appliedCoupon.discountValue,
        appliedCoupon.minimumOrderAmount,
        appliedCoupon.maximumDiscount
      );
      discountAmount = calculatedDiscount.discountAmount;
    }
    const discountedSubtotal = subtotal - discountAmount;
    const packagingCharge = calculatePackagingCharge(discountedSubtotal);
    const finalTotal = discountedSubtotal + packagingCharge;

    // Check minimum order amount
    if (finalTotal < 3000) {
      showToast('Minimum order amount is ₹3,000. Please add more items to your cart.', 'error');
      return;
    }

    setIsProcessing(true);

    try {
      // Create order data
      const orderData = {
        items: items,
        shippingAddress: {
          street: selectedAddress!.street,
          city: selectedAddress!.city,
          state: selectedAddress!.state,
          zipCode: selectedAddress!.zipCode,
          country: selectedAddress!.country || 'India'
        },
        paymentMethod: 'cash' as PaymentMethod, // Use 'cash' for COD as per backend enum
        pickupLocation: selectedPickupLocation || undefined,
        pickupInstructions: pickupInstructions || undefined,
        customerNotes: customerNotes || undefined,
        couponCode: appliedCoupon?.code,
        couponDiscount: discountAmount,
        packagingCharge: packagingCharge
      };

      // Call order creation API
      const response = await orderService.createOrder(orderData);
      
      if (response.success && response.data) {
        // Track purchase/enquiry event
        gtagService.trackPurchase({
          orderId: response.data.orderNumber,
          total: finalTotal,
          items: items,
          shipping: 0,
          tax: 0,
          coupon: appliedCoupon?.code
        });
        
        // Track enquiry submission (custom event)
        gtagService.trackEnquirySubmit({
          orderId: response.data.orderNumber,
          total: finalTotal,
          itemCount: items.length,
          hasPickupLocation: !!selectedPickupLocation
        });
        
        // Clear cart and stored coupon
        clearCart();
        couponService.clearStoredCoupon();
        
        // Navigate to success page with order number
        navigate(`/enquiry-success?enquiryId=${response.data.orderNumber}`);
      } else {
        throw new Error(response.error || 'Failed to submit enquiry');
      }
    } catch (error) {
      console.error('Order submission error:', error);
      showToast(
        error instanceof Error ? error.message : 'There was an error submitting your enquiry. Please try again.',
        'error'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="container py-16 max-w-4xl">
        <div className="text-center">
          <div className="text-6xl sm:text-8xl mb-6 sm:mb-8">🛒</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 mb-4">Your cart is empty</h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8">Add some items to your cart before checking out!</p>
          <Link
            to="/"
            className="bg-accent-600 text-white px-6 py-3 sm:px-8 sm:py-4 text-base sm:text-lg font-semibold rounded-xl hover:bg-accent-700 transition-all duration-300 inline-block hover:scale-105 hover:shadow-lg hover:shadow-accent-500/25"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  const subtotal = total;
  
  // Calculate discount
  let discountAmount = 0;
  if (appliedCoupon && appliedCoupon.isValid) {
    const calculatedDiscount = couponService.calculateDiscount(
      subtotal,
      appliedCoupon.discountType,
      appliedCoupon.discountValue,
      appliedCoupon.minimumOrderAmount,
      appliedCoupon.maximumDiscount
    );
    discountAmount = calculatedDiscount.discountAmount;
  }
  
  // Calculate packaging charge based on subtotal
  const calculatePackagingCharge = (amount: number): number => {
    if (!companyInfo?.shippingSettings?.packagingCharges) {
      return 0;
    }
    
    const applicableCharge = companyInfo.shippingSettings.packagingCharges.find(
      charge => amount >= charge.minAmount && amount <= charge.maxAmount
    );
    
    return applicableCharge?.charge || 0;
  };
  
  const discountedSubtotal = subtotal - discountAmount;
  const packagingCharge = calculatePackagingCharge(discountedSubtotal);
  const finalTotal = discountedSubtotal + packagingCharge;
  const minimumOrderAmount = 3000;
  const isMinimumOrderMet = finalTotal >= minimumOrderAmount;

  return (
    <div className="container py-8 sm:py-12 lg:py-16 max-w-6xl">
      {/* Coupon Success Popper */}
      <CouponSuccessPopper
        show={showCouponPopper}
        amount={popperData.amount}
        couponCode={popperData.code}
        onComplete={() => setShowCouponPopper(false)}
      />
      
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 mb-4">Submit Enquiry</h1>
        <div className="flex items-center text-sm text-gray-600">
          <Link to="/cart" className="hover:text-neutral-900">Cart</Link>
          <span className="mx-2">→</span>
          <span className="text-neutral-900 font-semibold">Submit Enquiry</span>
        </div>
      </div>

      {/* Delivery Address - Full Width */}
      <div className="mb-8">
        <div className="bg-white rounded-lg p-4 sm:p-6 shadow-md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-neutral-900">Delivery Address</h2>
            {selectedAddress && (
              <button
                type="button"
                onClick={() => setShowAddressBook(true)}
                className="text-accent-500 hover:text-accent-600 font-medium text-sm"
              >
                Change Address
              </button>
            )}
          </div>
          
          {selectedAddress && !showAddressBook ? (
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-gray-900">{selectedAddress.label}</h3>
                    {selectedAddress.isDefault && (
                      <span className="text-xs px-2 py-1 bg-accent-600 text-white rounded-full font-medium">
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-gray-700 text-sm">
                    {selectedAddress.street}, {selectedAddress.city}, {selectedAddress.state} - {selectedAddress.zipCode}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-neutral-200 rounded-xl p-6">
              {addresses.length === 0 ? (
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-neutral-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 616 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">Add your delivery address</h3>
                  <p className="text-sm text-neutral-600 mb-6">Add your first address to continue with checkout</p>
                </div>
              ) : (
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">Choose delivery address</h3>
                  <p className="text-sm text-neutral-600">Select from saved addresses or add a new one:</p>
                </div>
              )}
              <AddressBook 
                onAddressSelect={handleAddressSelect}
                showSelection={true}
              />
            </div>
          )}
        </div>
      </div>

      {/* Pickup Details - Full Width */}
      <div className="mb-8">
        <div className="bg-white rounded-lg p-4 sm:p-6 shadow-md">
          <h2 className="text-xl font-bold text-neutral-900 mb-4">Pickup Details *</h2>
          
          {/* Transport Provider Selection */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-neutral-800 mb-3">Select Transport Provider</h3>
            <div className="space-y-2">
              <div
                onClick={() => {
                  setSelectedTransportProvider('Mettur Transports');
                  // Clear pickup location when transport provider changes
                  setSelectedPickupLocation(null);
                  setPickupInstructions('');
                }}
                className={`relative rounded-lg border-2 p-3 cursor-pointer transition-all ${
                  selectedTransportProvider === 'Mettur Transports'
                    ? 'border-accent-500 bg-accent-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedTransportProvider === 'Mettur Transports'
                      ? 'border-accent-500'
                      : 'border-gray-300'
                  }`}>
                    {selectedTransportProvider === 'Mettur Transports' && (
                      <div className="w-3 h-3 rounded-full bg-accent-500"></div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Mettur Transports</h4>
                    <p className="text-sm text-gray-600">Reliable transport service with wide coverage</p>
                  </div>
                </div>
              </div>
              
              <div
                onClick={() => {
                  setSelectedTransportProvider('Rathimeena Speedparcel');
                  // Clear pickup location when transport provider changes
                  setSelectedPickupLocation(null);
                  setPickupInstructions('');
                }}
                className={`relative rounded-lg border-2 p-3 cursor-pointer transition-all ${
                  selectedTransportProvider === 'Rathimeena Speedparcel'
                    ? 'border-accent-500 bg-accent-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedTransportProvider === 'Rathimeena Speedparcel'
                      ? 'border-accent-500'
                      : 'border-gray-300'
                  }`}>
                    {selectedTransportProvider === 'Rathimeena Speedparcel' && (
                      <div className="w-3 h-3 rounded-full bg-accent-500"></div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Rathimeena Speedparcel</h4>
                    <p className="text-sm text-gray-600">Fast delivery with express options available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Pickup Location Selection - Only show after transport provider is selected */}
        
            <div className="mt-4">
              <h3 className="text-lg font-semibold text-neutral-800 mb-3">Select Pickup Location</h3>
              <div className="max-h-[500px] overflow-y-auto pr-2">
                <PickupLocationSelector
                  selectedLocationId={selectedPickupLocation}
                  transportProvider={selectedTransportProvider}
                  onLocationSelect={(id) => {
                    setSelectedPickupLocation(id);
                  }}
                />
              </div>
              
              {/* Pickup Instructions Field - Always show when transport provider is selected */}
              <div className="mt-4">
                <label htmlFor="pickupInstructions" className="block text-sm font-medium text-gray-700 mb-2">
                  Pickup Instructions {!selectedPickupLocation && "(Required if no location selected)"}
                </label>
                <textarea
                  id="pickupInstructions"
                  value={pickupInstructions}
                  onChange={(e) => setPickupInstructions(e.target.value)}
                  placeholder="Add any special instructions for pickup (e.g., timing preferences, contact person, specific location details...)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 resize-none"
                  rows={selectedPickupLocation ? 3 : 4}
                  maxLength={500}
                />
                <div className="flex justify-between items-center mt-1">
                  <p className="text-xs text-gray-500">
                    {selectedPickupLocation 
                      ? "Help the transport team with specific pickup instructions"
                      : "Please provide detailed pickup information as no location is selected"
                    }
                  </p>
                  <p className="text-xs text-gray-400">
                    {pickupInstructions.length}/500
                  </p>
                </div>
              </div>
            </div>
          
        </div>
      </div>

      {/* Coupon Code Section */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg p-6 shadow-md mb-6">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">Have a Coupon?</h2>
            
            {!appliedCoupon ? (
              <div className="space-y-4">
                <div className="flex gap-3">
                  <input
                    type="text"
                    name="couponCode"
                    value={formData.couponCode}
                    onChange={handleInputChange}
                    placeholder="Enter coupon code"
                    className={`flex-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-accent-500 focus:border-accent-500 ${
                      errors.coupon ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={handleApplyCoupon}
                    disabled={couponLoading}
                    className="px-6 py-2 bg-accent-600 text-white font-medium rounded-md hover:bg-accent-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {couponLoading ? 'Validating...' : 'Apply'}
                  </button>
                </div>
                {errors.coupon && <p className="text-red-500 text-sm">{errors.coupon}</p>}
                
                {/* Available coupons hint - generic message */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800 font-medium">
                    Enter a coupon code to get discount on your order
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    Coupons are available for special occasions and loyal customers
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                      <span className="font-semibold text-green-800">
                        Coupon Applied: {appliedCoupon.code}
                      </span>
                    </div>
                    <p className="text-sm text-green-700 mt-1">
                      You saved ₹{discountAmount.toLocaleString()} 
                      {appliedCoupon.discountType === 'percentage' && ` (${appliedCoupon.discountValue}% off)`}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveCoupon}
                    className="text-red-600 hover:text-red-700 font-medium text-sm"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}
        </div>

        {/* Enquiry Summary - Full Width */}
        <div className="bg-white rounded-lg p-6 shadow-md">
          <h2 className="text-xl font-bold text-neutral-900 mb-4">Enquiry Summary</h2>
          
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto mb-6">
            <table className="w-full">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-3 text-sm font-semibold text-neutral-700">Product</th>
                  <th className="pb-3 text-sm font-semibold text-neutral-700 text-center">Price</th>
                  <th className="pb-3 text-sm font-semibold text-neutral-700 text-center">Quantity</th>
                  <th className="pb-3 text-sm font-semibold text-neutral-700 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b">
                    <td className="py-4">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 flex-shrink-0">
                          <LazyImage
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover rounded-md"
                          />
                        </div>
                        <div>
                          <h3 className="font-semibold text-sm">{item.name}</h3>
                          {item.sku && <p className="text-xs text-gray-500">SKU: {item.sku}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 text-center">
                      <p className="text-sm">₹{item.price.toLocaleString()}</p>
                    </td>
                    <td className="py-4 text-center">
                      <p className="text-sm font-medium">{item.quantity}</p>
                    </td>
                    <td className="py-4 text-right">
                      <p className="font-bold">₹{(item.price * item.quantity).toLocaleString()}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4 mb-6">
            {items.map((item) => (
              <div key={item.id} className="border rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-16 h-16 flex-shrink-0">
                    <LazyImage
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover rounded-md"
                    />
                  </div>
                  <div className="flex-grow">
                    <h3 className="font-semibold text-sm">{item.name}</h3>
                    {item.sku && <p className="text-xs text-gray-500">SKU: {item.sku}</p>}
                    <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">Price:</span>
                        <p className="font-medium">₹{item.price.toLocaleString()}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Qty:</span>
                        <p className="font-medium">{item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-gray-600">Total:</span>
                        <p className="font-bold">₹{(item.price * item.quantity).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary Section */}
          <div className="border-t pt-4">
            <div className="max-w-md ml-auto space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">₹{subtotal.toLocaleString()}</span>
              </div>
              {appliedCoupon && discountAmount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount ({appliedCoupon.code})</span>
                  <span>-₹{discountAmount.toLocaleString()}</span>
                </div>
              )}
              {packagingCharge > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Packaging Charge</span>
                  <span className="font-medium">₹{packagingCharge.toLocaleString()}</span>
                </div>
              )}
              <div className="mt-3 mb-2">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-full animate-pulse">
                  <svg className="w-5 h-5 text-blue-600 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium text-blue-800">
                    Shipping charges will be decided by the lorry service
                  </span>
                </div>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="text-lg font-bold text-neutral-900">Total</span>
                  <span className="text-lg font-bold text-neutral-900">₹{finalTotal.toLocaleString()}</span>
                </div>
                {/* Minimum Order Progress */}
                {!isMinimumOrderMet && (
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Min. order: ₹{minimumOrderAmount.toLocaleString()}</span>
                      <span>₹{(minimumOrderAmount - finalTotal).toLocaleString()} more needed</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-amber-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min((finalTotal / minimumOrderAmount) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Customer Notes - Full Width */}
        <div className="bg-white rounded-lg p-6 shadow-md mb-6">
          <h2 className="text-xl font-bold text-neutral-900 mb-4">Additional Notes (Optional)</h2>
          <textarea
            value={customerNotes}
            onChange={(e) => setCustomerNotes(e.target.value)}
            placeholder="Add any special requests or additional information for your order..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 resize-none"
            rows={4}
            maxLength={1000}
          />
          <div className="flex justify-between items-center mt-1">
            <p className="text-xs text-gray-500">
              Share any specific requirements, preferences, or delivery instructions
            </p>
            <p className="text-xs text-gray-400">
              {customerNotes.length}/1000
            </p>
          </div>
        </div>

        {/* Minimum Order Message */}
        {!isMinimumOrderMet && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-amber-800">
                Minimum order amount is <span className="font-semibold">₹{minimumOrderAmount.toLocaleString()}</span>. 
                Please add <span className="font-semibold">₹{(minimumOrderAmount - finalTotal).toLocaleString()}</span> more to your cart.
              </p>
            </div>
          </div>
        )}

        {/* Submit Enquiry - Full Width */}
        <button
          type="submit"
          disabled={isProcessing || !selectedTransportProvider || !isMinimumOrderMet}
          className={`w-full py-4 rounded-xl font-semibold text-lg transition-all duration-300 ${
            isProcessing || !selectedTransportProvider || !isMinimumOrderMet
              ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
              : 'bg-accent-600 text-white hover:bg-accent-700 hover:scale-[1.02] hover:shadow-lg hover:shadow-accent-500/25'
          }`}
        >
          {isProcessing 
            ? 'Submitting Enquiry...' 
            : !selectedTransportProvider
              ? 'Please Select Transport Provider'
              : !isMinimumOrderMet
              ? `Minimum Order ₹${minimumOrderAmount.toLocaleString()} Required`
              : `Submit Enquiry - ₹${finalTotal.toLocaleString()}`
          }
        </button>
      </form>
    </div>
  );
};

export default Checkout;